import { Component, type ReactNode, type ErrorInfo } from "react";
import { AlertCircle, RotateCw } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, info: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info);
    this.setState({ info });
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <div className="w-full max-w-lg rounded-2xl border border-danger/30 bg-danger/5 p-6 shadow-premium">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-1 h-5 w-5 shrink-0 text-danger" />
              <div className="min-w-0 flex-1">
                <h1 className="font-display text-lg font-semibold text-danger">
                  Errore di runtime
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Qualcosa è andato storto durante il render. Manda questo screenshot
                  al team di sviluppo.
                </p>
                <div className="mt-3 max-h-40 overflow-auto rounded-md bg-black/30 p-2 font-mono text-[11px] leading-relaxed text-danger">
                  {this.state.error.message}
                </div>
                {this.state.info?.componentStack && (
                  <details className="mt-2 text-[10px]">
                    <summary className="cursor-pointer text-muted-foreground">
                      Stack trace
                    </summary>
                    <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-black/30 p-2 font-mono leading-tight text-muted-foreground">
                      {this.state.info.componentStack}
                    </pre>
                  </details>
                )}
                <button
                  type="button"
                  onClick={() => {
                    this.setState({ error: null, info: null });
                    window.location.reload();
                  }}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                >
                  <RotateCw className="h-3.5 w-3.5" />
                  Ricarica pagina
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
